import React from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate, Link } from "react-router-dom";
import "@/App.css";
import TopNav from "@/components/TopNav";
import RouteAnalytics from "@/components/RouteAnalytics";
import Home from "@/pages/Home";
import About from "@/pages/About";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsOfService from "@/pages/TermsOfService";
import CommunityGuidelines from "@/pages/CommunityGuidelines";
import PreviousPolls from "@/pages/PreviousPolls";
import MessageDetail from "@/pages/MessageDetail";
import PollDetail from "@/pages/PollDetail";
import ArchivedKeralaCM from "@/pages/ArchivedKeralaCM";
import NotFound from "@/pages/NotFound";
import AdminLogin from "@/pages/admin/Login";
import AdminAuthCallback from "@/pages/admin/AuthCallback";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/Dashboard";
import AdminActiveQuestions from "@/pages/admin/ActiveQuestions";
import AdminMessages from "@/pages/admin/Messages";
import AdminModeration from "@/pages/admin/Moderation";
import AdminSpam from "@/pages/admin/Spam";
import AdminArchives from "@/pages/admin/Archives";
import AdminAnalytics from "@/pages/admin/Analytics";
import AdminReports from "@/pages/admin/Reports";
import AdminContentStudio from "@/pages/admin/ContentStudio";
import AdminNewsletter from "@/pages/admin/Newsletter";
import AdminContact from "@/pages/admin/Contact";
import AdminLoginAttempts from "@/pages/admin/LoginAttempts";
import AdminSettings from "@/pages/admin/Settings";
import { useLang } from "@/lib/useLang";

const FOOTER_LINKS = [
  { to: "/about", label: "About" },
  { to: "/previous-polls", label: "Previous Polls" },
  { to: "/about#faq", label: "FAQ" },
  { to: "/privacy", label: "Privacy Policy" },
  { to: "/terms", label: "Terms" },
  { to: "/community-guidelines", label: "Community Guidelines" },
];

const SiteFooter = () => {
  const { t } = useLang();
  return (
    <footer className="border-t-4 border-black mt-12 md:mt-16" data-testid="site-footer" style={{ background: "#0A0A0A", color: "#FDFBF7" }}>
      <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-12 py-10 md:py-12">
        <nav className="flex flex-wrap gap-x-6 gap-y-3 mb-6" data-testid="site-footer-nav">
          {FOOTER_LINKS.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className="text-xs uppercase font-extrabold tracking-[0.25em] hover:opacity-70 transition-opacity"
              style={{ color: "#FDFBF7" }}
              data-testid={`footer-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {link.label}
            </Link>
          ))}
        </nav>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 pt-6 border-t border-neutral-700/40">
          <p className="text-xs uppercase font-bold tracking-[0.3em]" data-testid="footer-left">
            {t("footer_left")}
          </p>
          <p className="text-xs uppercase font-bold tracking-[0.3em] text-neutral-500" data-testid="footer-right">
            {t("footer_right")}
          </p>
        </div>
      </div>
    </footer>
  );
};

const PublicShell = ({ children }) => {
  const location = useLocation();
  const hideSiteFooter = location.pathname.startsWith("/previous-polls/");
  return (
    <>
      <TopNav />
      {children}
      {!hideSiteFooter && <SiteFooter />}
    </>
  );
};

function App() {
  return (
    <BrowserRouter>
      <RouteAnalytics />
      <Routes>
        {/* Admin routes — no public chrome */}
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin/auth-callback" element={<AdminAuthCallback />} />
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<Navigate to="/admin/dashboard" replace />} />
          <Route path="dashboard" element={<AdminDashboard />} />
          <Route path="active-questions" element={<AdminActiveQuestions />} />
          <Route path="messages" element={<AdminMessages />} />
          <Route path="moderation" element={<AdminModeration />} />
          <Route path="spam" element={<AdminSpam />} />
          <Route path="archives" element={<AdminArchives />} />
          <Route path="analytics" element={<AdminAnalytics />} />
          <Route path="reports" element={<AdminReports />} />
          <Route path="content-studio" element={<AdminContentStudio />} />
          <Route path="newsletter" element={<AdminNewsletter />} />
          <Route path="contact" element={<AdminContact />} />
          <Route path="login-attempts" element={<AdminLoginAttempts />} />
          <Route path="settings" element={<AdminSettings />} />
        </Route>

        {/* Public site */}
        <Route
          path="/*"
          element={
            <PublicShell>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/about" element={<About />} />
                <Route path="/faq" element={<Navigate to="/about#faq" replace />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<TermsOfService />} />
                <Route path="/community-guidelines" element={<CommunityGuidelines />} />
                <Route path="/previous-polls" element={<PreviousPolls />} />
                <Route path="/previous-polls/kerala-cm-2026" element={<ArchivedKeralaCM />} />
                <Route path="/m/:id" element={<MessageDetail />} />
                <Route path="/p/:slug" element={<PollDetail />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </PublicShell>
          }
        />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
